import { lazy } from 'react';
import { DashboardSkeleton } from '../components/SkeletonLoader';

// Loading fallback component
export const RouteLoadingFallback = () => <DashboardSkeleton />;

// Minimal fallback for quick loads
export const MinimalLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// ============================================================================
// AUTHENTICATION & LANDING PAGES (High Priority - Quick Load)
// ============================================================================
export const LandingPage = lazy(() => import('../pages/LandingPage'));
export const AuthPage = lazy(() => import('../pages/AuthPage'));
export const OnboardingPage = lazy(() => import('../pages/OnboardingPage'));

// ============================================================================
// MARKETING PAGES (Medium Priority - Grouped Chunk)
// ============================================================================
// These are loaded together as they share similar dependencies
export const MarketingPages = {
  PricingPage: lazy(() =>
    import(/* webpackChunkName: "marketing" */ '../pages/PricingPage')
  ),
  FeaturesPage: lazy(() =>
    import(/* webpackChunkName: "marketing" */ '../pages/FeaturesPage')
  ),
  AboutPage: lazy(() =>
    import(/* webpackChunkName: "marketing" */ '../pages/AboutPage')
  ),
  ContactPage: lazy(() =>
    import(/* webpackChunkName: "marketing" */ '../pages/ContactPage')
  ),
};

// ============================================================================
// LEGAL PAGES (Low Priority - Grouped Chunk)
// ============================================================================
export const LegalPages = {
  PrivacyPolicyPage: lazy(() =>
    import(/* webpackChunkName: "legal" */ '../pages/PrivacyPolicyPage')
  ),
  TermsOfServicePage: lazy(() =>
    import(/* webpackChunkName: "legal" */ '../pages/TermsOfServicePage')
  ),
  CookiePolicyPage: lazy(() =>
    import(/* webpackChunkName: "legal" */ '../pages/CookiePolicyPage')
  ),
  GDPRCompliancePage: lazy(() =>
    import(/* webpackChunkName: "legal" */ '../pages/GDPRCompliancePage')
  ),
};

// ============================================================================
// APPLICATION CORE PAGES (High Priority - Main Application)
// ============================================================================
export const DashboardPage = lazy(() =>
  import(/* webpackChunkName: "app-core" */ '../pages/DashboardPage')
);

export const InvoicesPage = lazy(() =>
  import(/* webpackChunkName: "app-core" */ '../pages/InvoicesPage')
);

export const CustomersPage = lazy(() =>
  import(/* webpackChunkName: "app-core" */ '../pages/CustomersPage')
);

export const SettingsPage = lazy(() =>
  import(/* webpackChunkName: "app-core" */ '../pages/SettingsPage')
);

// ============================================================================
// FINANCIAL PAGES (Medium Priority - Grouped Chunk)
// ============================================================================
export const FinancialPages = {
  PaymentsPage: lazy(() =>
    import(/* webpackChunkName: "financial" */ '../pages/PaymentsPage')
  ),
  AnalyticsPage: lazy(() =>
    import(/* webpackChunkName: "financial" */ '../pages/AnalyticsPage')
  ),
  DocumentsPage: lazy(() =>
    import(/* webpackChunkName: "financial" */ '../pages/DocumentsPage')
  ),
  RecurringInvoicesPage: lazy(() =>
    import(/* webpackChunkName: "financial" */ '../pages/RecurringInvoicesPage')
  ),
};

// ============================================================================
// INVOICE COMPONENTS (Medium Priority - Grouped Chunk)
// ============================================================================
export const InvoiceComponents = {
  RealInvoiceForm: lazy(() =>
    import(/* webpackChunkName: "invoices" */ '../components/RealInvoiceForm')
  ),
};

// ============================================================================
// ADMIN PAGES (Low Priority - Lazy Load)
// ============================================================================
export const AdminPages = {
  AdminPage: lazy(() =>
    import(/* webpackChunkName: "admin" */ '../pages/AdminPage')
  ),
  SuperAdminPage: lazy(() =>
    import(/* webpackChunkName: "admin" */ '../pages/SuperAdminPage')
  ),
  TenantManagementPage: lazy(() =>
    import(/* webpackChunkName: "admin" */ '../pages/TenantManagementPage')
  ),
  PlatformConfigPage: lazy(() =>
    import(/* webpackChunkName: "admin" */ '../pages/PlatformConfigPage')
  ),
  UserManagementPage: lazy(() =>
    import(/* webpackChunkName: "admin" */ '../pages/UserManagementPage')
  ),
  BillingManagementPage: lazy(() =>
    import(/* webpackChunkName: "admin" */ '../pages/BillingManagementPage')
  ),
  SystemHealthPage: lazy(() =>
    import(/* webpackChunkName: "admin" */ '../pages/SystemHealthPage')
  ),
  SecurityCenterPage: lazy(() =>
    import(/* webpackChunkName: "admin" */ '../pages/SecurityCenterPage')
  ),
};

// ============================================================================
// UTILITY PAGES (Low Priority - Lazy Load)
// ============================================================================
export const UtilityPages = {
  SecurityPage: lazy(() =>
    import(/* webpackChunkName: "utilities" */ '../pages/SecurityPage')
  ),
};

// ============================================================================
// LAYOUT COMPONENTS (Medium Priority)
// ============================================================================
export const SidebarLayout = lazy(() =>
  import(/* webpackChunkName: "layout" */ '../components/layout/SidebarLayout')
);

// ============================================================================
// ROUTE PREFETCHING UTILITIES
// ============================================================================

/**
 * Prefetch a route component to improve perceived performance
 * @param {Function} lazyComponent - The lazy-loaded component to prefetch
 */
export const prefetchRoute = (lazyComponent) => {
  if (typeof lazyComponent === 'function' && lazyComponent._payload) {
    // This will start loading the component
    lazyComponent._payload._result = lazyComponent._payload._result || lazyComponent._payload._result();
  }
};

/**
 * Prefetch multiple routes at once
 * @param {Array<Function>} lazyComponents - Array of lazy-loaded components
 */
export const prefetchRoutes = (lazyComponents) => {
  lazyComponents.forEach(component => prefetchRoute(component));
};

/**
 * Prefetch based on user role/context
 * @param {string} userRole - User role (admin, customer, etc.)
 */
export const prefetchByRole = (userRole) => {
  if (userRole === 'admin' || userRole === 'superadmin') {
    // Prefetch admin pages
    prefetchRoutes([
      AdminPages.AdminPage,
      AdminPages.SuperAdminPage,
      AdminPages.TenantManagementPage,
    ]);
  } else {
    // Prefetch common customer pages
    prefetchRoutes([
      DashboardPage,
      InvoicesPage,
      CustomersPage,
    ]);
  }
};

/**
 * Prefetch on hover - attach to navigation links
 * @param {Function} lazyComponent - Component to prefetch
 * @returns {Object} - Event handlers for onMouseEnter and onFocus
 */
export const usePrefetchOnHover = (lazyComponent) => {
  return {
    onMouseEnter: () => prefetchRoute(lazyComponent),
    onFocus: () => prefetchRoute(lazyComponent),
  };
};

// ============================================================================
// PRELOAD CRITICAL ROUTES
// ============================================================================

/**
 * Preload critical application routes on idle
 */
export const preloadCriticalRoutes = () => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      prefetchRoutes([
        DashboardPage,
        InvoicesPage,
        CustomersPage,
        SettingsPage,
      ]);
    }, { timeout: 2000 });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      prefetchRoutes([
        DashboardPage,
        InvoicesPage,
        CustomersPage,
        SettingsPage,
      ]);
    }, 1000);
  }
};
